import { DataRequestDetail } from "../../../common/models";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { Api } from "../../../common/service/Api";
import { createDataRequestZip, getZipFileCount } from "../../util/accessRequestZipUtils";
import { DataRequestAttachment } from "../../hooks/useDataRequestAttachments";
import { PersonalIdentifiersData } from "../../components/dataRequest/stayCompliant/DataRequestProcessingContext";

interface AccessRequestNextStepParams {
  request: DataRequestDetail;
  org: OrganizationDto;
  attachments: DataRequestAttachment[];
  personalIdentifiers: PersonalIdentifiersData;
  setGeneratedZip: (zip: { file: File; fileCount: number }) => void;
  attachment: {
    files?: File[];
    addFiles: (files: File[]) => void;
    removeFile: (index: number) => void;
  } | null;
}

interface ExtractedCustomField {
  label: string;
  value: string;
}

/**
 * Extracts custom fields from the request, excluding address-related fields
 */
function extractCustomFields(
  request: DataRequestDetail,
  org: OrganizationDto,
): ExtractedCustomField[] {
  const customFields: ExtractedCustomField[] = [];
  const excludedKeys = new Set([
    "address1",
    "address2",
    "city",
    "state",
    "zipCode",
    "country",
    "phoneNumber",
  ]);

  if (request.organizationCustomInput && org.customFieldEnabled && org.customFieldLabel) {
    customFields.push({
      label: org.customFieldLabel,
      value: request.organizationCustomInput,
    });
  }

  if (request.subjectCustomFieldsFmt) {
    Object.entries(request.subjectCustomFieldsFmt).forEach(([label, value]) => {
      const keyToCheck = label
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[()]/g, "")
        .replace(/line1/g, "1")
        .replace(/line2/g, "2");

      const isExcluded =
        excludedKeys.has(keyToCheck) ||
        label.toLowerCase().includes("address") ||
        label.toLowerCase().includes("city") ||
        label.toLowerCase().includes("state") ||
        label.toLowerCase().includes("zip") ||
        label.toLowerCase().includes("country") ||
        label.toLowerCase().includes("phone");

      if (!isExcluded && value) {
        customFields.push({ label, value: String(value) });
      }
    });
  }

  return customFields;
}

/**
 * Extracts phone number from all possible sources in the request
 */
function extractPhoneNumber(request: DataRequestDetail): string {
  if (request.subjectFreePhone) {
    return request.subjectFreePhone;
  }

  if (request.subjectPhoneNumber) {
    return request.subjectPhoneNumber;
  }

  if (request.subjectCustomFields?.phoneNumber) {
    return request.subjectCustomFields.phoneNumber;
  }

  if (request.subjectCustomFieldsFmt) {
    const phoneField = Object.entries(request.subjectCustomFieldsFmt).find(([label]) =>
      label.toLowerCase().includes("phone"),
    );
    if (phoneField) {
      return phoneField[1] as string;
    }
  }

  return "";
}

/**
 * Extracts address from all possible sources in the request
 */
function extractAddress(request: DataRequestDetail): string {
  if (request.subjectFreeTextAddress) {
    return request.subjectFreeTextAddress;
  }

  if (request.subjectMailingAddress) {
    return request.subjectMailingAddress;
  }

  if (request.subjectCustomFields) {
    const addressParts = [
      request.subjectCustomFields["address1"],
      request.subjectCustomFields["address2"],
      request.subjectCustomFields["city"],
      request.subjectCustomFields["state"],
      request.subjectCustomFields["zipCode"],
      request.subjectCustomFields["country"],
    ].filter(Boolean);

    if (addressParts.length > 0) {
      return addressParts.join(", ");
    }
  }

  return "";
}

/**
 * Reads form values directly from the DOM as a last resort
 */
function getDomFormValues(): Record<string, string | undefined> {
  const getDomValue = (name: string) =>
    (
      document.querySelector(`input[name="${name}"], textarea[name="${name}"]`) as
        | HTMLInputElement
        | HTMLTextAreaElement
    )?.value || undefined;

  return {
    name: getDomValue("name"),
    email: getDomValue("email"),
    phone: getDomValue("phone"),
    postalAddress: getDomValue("postalAddress"),
    onlineIdentifiers: getDomValue("onlineIdentifiers"),
  };
}

/**
 * Gets stored personal identifiers from localStorage
 */
function getStoredIdentifiers(requestId: string): Record<string, any> | undefined {
  try {
    const key = `request-${requestId}-personal-identifiers`;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Builds the form data by merging values from multiple sources
 * Priority: DOM values > localStorage > context > fallback from request
 */
function buildFormData(
  request: DataRequestDetail,
  org: OrganizationDto,
  personalIdentifiers: PersonalIdentifiersData,
): Record<string, any> {
  const extractedCustomFields = extractCustomFields(request, org);
  const extractedPhone = extractPhoneNumber(request);
  const extractedAddress = extractAddress(request);

  const fallbackData = {
    name: `${request.subjectFirstName || ""} ${request.subjectLastName || ""}`.trim(),
    email: request.subjectEmailAddress || "",
    phone: extractedPhone,
    subjectFreePhone: extractedPhone,
    postalAddress: extractedAddress,
    subjectFreeTextAddress: extractedAddress,
    onlineIdentifiers: request.subjectOnlineIdentifiers || "",
    customFields: extractedCustomFields,
  };

  const stored = getStoredIdentifiers(request.id);
  const domValues = getDomFormValues();

  return {
    name: domValues.name || stored?.name || (personalIdentifiers as any)?.name || fallbackData.name,
    email: domValues.email || stored?.email || personalIdentifiers?.email || fallbackData.email,
    phone: domValues.phone || stored?.phone || personalIdentifiers?.phone || fallbackData.phone,
    subjectFreePhone:
      domValues.phone || stored?.phone || personalIdentifiers?.phone || fallbackData.phone,
    postalAddress:
      domValues.postalAddress ||
      stored?.postalAddress ||
      personalIdentifiers?.postalAddress ||
      fallbackData.postalAddress,
    subjectFreeTextAddress:
      domValues.postalAddress ||
      stored?.postalAddress ||
      personalIdentifiers?.postalAddress ||
      fallbackData.postalAddress,
    onlineIdentifiers:
      domValues.onlineIdentifiers ||
      stored?.onlineIdentifiers ||
      personalIdentifiers?.onlineIdentifiers ||
      fallbackData.onlineIdentifiers,
    customFields:
      stored?.customFields || personalIdentifiers?.customFields || fallbackData.customFields,
  };
}

/**
 * Persists the form data to localStorage for use in subsequent steps
 */
function persistFormDataToLocalStorage(requestId: string, formData: Record<string, any>): void {
  try {
    localStorage.setItem(`request-${requestId}-zip-formData`, JSON.stringify(formData));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Persists the latest identifiers to the backend
 */
async function persistIdentifiersToBackend(
  orgId: string,
  request: DataRequestDetail,
  formData: Record<string, any>,
): Promise<void> {
  try {
    await Api.dataRequest.updateRequest(orgId, request.id, {
      requestDate: request.requestDate,
      subjectFirstName: formData.name.split(" ")[0] || "",
      subjectLastName: formData.name.split(" ").slice(1).join(" ") || "",
      subjectEmailAddress: formData.email,
      subjectPhoneNumber: formData.phone || "",
      subjectFreePhone: formData.subjectFreePhone || "",
      subjectMailingAddress: formData.postalAddress || "",
      subjectFreeTextAddress: formData.subjectFreeTextAddress || "",
      submissionMethod: request.submissionMethod || "MANUAL",
      dataRequestType: request.dataRequestType,
      subjectOnlineIdentifiers: formData.onlineIdentifiers?.trim() || "",
    });
  } catch {
    // non-blocking; continue to zip even if persistence fails
  }
}

/**
 * Force-blur the active element to commit any pending form value updates
 */
function blurActiveElement(): void {
  try {
    const active = (document?.activeElement as HTMLElement) || null;
    if (active && typeof active.blur === "function") {
      active.blur();
    }
  } catch {
    // Ignore blur errors
  }
}

/**
 * Checks if the current step is transitioning from RETRIEVE_DATA for access requests
 */
export function isAccessRequestRetrieveDataStep(request: DataRequestDetail): boolean {
  return (
    request.state === "RETRIEVE_DATA" &&
    (request.dataRequestType.workflowType === "KNOW" ||
      request.dataRequestType.workflowType === "PORTABILITY")
  );
}

/**
 * Handles the access request next step transition from RETRIEVE_DATA to SEND_DATA.
 * Creates a zip file with the request data and attachments, updates the attachment state.
 */
export async function handleAccessRequestNextStep({
  request,
  org,
  attachments,
  personalIdentifiers,
  setGeneratedZip,
  attachment,
}: AccessRequestNextStepParams): Promise<void> {
  // Force-blur active input to commit value updates (final-form often updates on blur)
  blurActiveElement();

  // Allow any pending form updates to flush into context before reading
  await new Promise((r) => setTimeout(r, 0));

  // Build form data from all available sources
  const formData = buildFormData(request, org, personalIdentifiers);

  // Persist the exact payload used to generate the TXT, so the next step and previews can reuse it
  persistFormDataToLocalStorage(request.id, formData);

  // Persist latest identifiers to backend to ensure cross-device availability
  await persistIdentifiersToBackend(org.id, request, formData);

  // Create the zip file
  const requestorName =
    `${request.subjectFirstName || ""}${request.subjectLastName || ""}`.trim() || "requestor";
  const zipFile = await createDataRequestZip(requestorName, formData as any, attachments);
  const fileCount = getZipFileCount(attachments);
  setGeneratedZip({ file: zipFile, fileCount });

  // Clear any existing files and only keep the zip file
  // This prevents duplicate attachments (files are already in the zip)
  if (attachment) {
    const currentFileCount = attachment.files?.length || 0;
    for (let i = currentFileCount - 1; i >= 0; i--) {
      attachment.removeFile(i);
    }
    attachment.addFiles([zipFile]);
  }
}
